import { useEffect, useState } from 'react';
import { useAuth, hasFullAccess } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { openRazorpayCheckout, isRazorpayTestKey, pollPaymentRecover } from '../utils/razorpay';
import { trackEvent } from '../utils/analytics';
import { clearPaperCache } from '../utils/paperCache';
import SubscribeBillingNotice from '../components/SubscribeBillingNotice';
import { businessPhoneDisplay, businessTelHref, businessWhatsAppHref } from '../config/business';
import './SubscriptionPage.css';

import { API_URL as API } from '../config/env.js';

const BASE = 999;
const GST = +(BASE * 0.18).toFixed(2);
const TOTAL = +(BASE + GST).toFixed(2);

export default function SubscriptionPage({ embedded = false, onClose }) {
  const { t, lang } = useLang();
  const { user, logout, updateSubscription, syncUser } = useAuth();
  const [step, setStep] = useState('plan');
  const [successUntil, setSuccessUntil] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [razorpayTestMode, setRazorpayTestMode] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState(() => user?.phone || '');
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);
  const unlocked = hasFullAccess(user);

  function normalizePhoneInput(raw) {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 10) return digits;
    if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
    if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
    return digits;
  }

  useEffect(() => {
    if (!user?.token) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/status`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        syncUser({
          isSubscribed: data.isSubscribed,
          isAdmin: data.isAdmin,
          isOwner: data.isOwner,
          isPreview: data.isPreview,
          subscribedUntil: data.subscribedUntil,
        });
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.token, syncUser]);

  useEffect(() => {
    if (unlocked && embedded && onClose && step !== 'success') onClose();
  }, [unlocked, embedded, onClose, step]);

  async function verifyPayment(body) {
    const phone = normalizePhoneInput(whatsappPhone);
    const vRes = await fetch(`${API}/api/verify-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.token}`,
      },
      body: JSON.stringify({
        ...body,
        email: user.email,
        whatsappPhone: phone,
        whatsapp_opt_in: whatsappOptIn,
      }),
    });
    const vData = await vRes.json();
    if (!vRes.ok) throw new Error(vData.error || t.subVerifyFailed);

    // Clear any prior "locked" paper access so the reader can open immediately.
    clearPaperCache();
    updateSubscription(true, vData.subscribedUntil);
    syncUser({
      ...(vData.token ? { token: vData.token } : {}),
      isSubscribed: true,
      isTrial: false,
      subscribedUntil: vData.subscribedUntil,
    });

    // Double-check server status so localStorage never stays stale.
    try {
      const statusToken = vData.token || user.token;
      const sRes = await fetch(`${API}/api/auth/status`, {
        headers: { Authorization: `Bearer ${statusToken}` },
      });
      if (sRes.ok) {
        const status = await sRes.json();
        syncUser({
          isSubscribed: status.isSubscribed ?? true,
          isAdmin: status.isAdmin,
          isOwner: status.isOwner,
          isPreview: status.isPreview,
          subscribedUntil: status.subscribedUntil || vData.subscribedUntil,
          trialUntil: status.trialUntil,
          isTrial: status.isTrial,
        });
      }
    } catch {
      /* ignore — local unlock already applied */
    }

    trackEvent('purchase', {
      transaction_id: body.razorpay_order_id || body.orderId,
      value: TOTAL,
      currency: 'INR',
    });
    setSuccessUntil(vData.subscribedUntil);
    setReceiptUrl(vData.receiptUrl || '');
    setEmailSent(!!vData.emailSent);
    setStep('success');
  }

  function formatUntil(ts) {
    if (!ts) return '';
    const locale = lang === 'te' ? 'te-IN' : lang === 'ta' ? 'ta-IN' : lang === 'kn' ? 'kn-IN' : 'en-IN';
    return new Date(Number(ts)).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  async function handleSubscribe() {
    setLoading(true);
    setError('');
    const phone = normalizePhoneInput(whatsappPhone);
    if (phone.length !== 10) {
      setError(t.subWhatsappRequired || 'Enter a valid 10-digit WhatsApp number');
      setLoading(false);
      return;
    }
    if (!whatsappOptIn) {
      setError(t.subWhatsappOptInRequired || 'Please allow WhatsApp alerts for today’s paper');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/api/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({ email: user.email, phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t.subOrderError);
        setLoading(false);
        return;
      }

      const demo = !!data.demo;
      setIsDemoMode(demo);

      if (demo) {
        setStep('processing');
        setLoading(false);
        setTimeout(async () => {
          try {
            await verifyPayment({
              email: user.email,
              phone: user.phone || '',
              razorpay_order_id: data.order_id || data.orderId,
              demo: true,
            });
          } catch (err) {
            setError(err.message || t.subVerifyFailed);
            setStep('plan');
          }
        }, 1500);
        return;
      }

      const orderId = data.order_id || data.orderId;
      const keyId = data.keyId;
      setRazorpayTestMode(isRazorpayTestKey(keyId));
      if (!keyId || !orderId) {
        setError(t.subInvalidPayment);
        setLoading(false);
        return;
      }

      setLoading(false);

      try {
        const payment = await openRazorpayCheckout({
          keyId,
          orderId,
          amount: data.amount,
          currency: data.currency,
          name: user.name || 'Repatikosam Reader',
          email: user.email,
          description: `Annual subscription · ₹${TOTAL.toFixed(2)} incl. GST`,
        });

        setStep('processing');
        await verifyPayment({
          email: user.email,
          phone: user.phone || '',
          razorpay_order_id: payment.razorpay_order_id,
          razorpay_payment_id: payment.razorpay_payment_id,
          razorpay_signature: payment.razorpay_signature,
        });
      } catch (err) {
        // Bank sometimes marks failed then later authorizes — keep checking so paper unlocks.
        const recoverOrderId = err.orderId || orderId;
        const recoverPaymentId = err.paymentId || '';
        if (recoverOrderId && err.code !== 'cancelled') {
          setStep('processing');
          setError('');
          try {
            const recovered = await pollPaymentRecover({
              apiBase: API,
              token: user.token,
              orderId: recoverOrderId,
              paymentId: recoverPaymentId,
              attempts: 15,
              intervalMs: 4000,
            });
            if (recovered?.success && recovered.isSubscribed) {
              clearPaperCache();
              updateSubscription(true, recovered.subscribedUntil);
              syncUser({
                ...(recovered.token ? { token: recovered.token } : {}),
                isSubscribed: true,
                isTrial: false,
                subscribedUntil: recovered.subscribedUntil,
              });
              setSuccessUntil(recovered.subscribedUntil);
              setStep('success');
              return;
            }
          } catch {
            /* fall through to error */
          }
        } else if (err.code === 'cancelled' && recoverOrderId) {
          // Quick check in case money already went through before dismiss.
          try {
            const recovered = await pollPaymentRecover({
              apiBase: API,
              token: user.token,
              orderId: recoverOrderId,
              paymentId: recoverPaymentId,
              attempts: 3,
              intervalMs: 2000,
            });
            if (recovered?.success && recovered.isSubscribed) {
              clearPaperCache();
              updateSubscription(true, recovered.subscribedUntil);
              syncUser({
                ...(recovered.token ? { token: recovered.token } : {}),
                isSubscribed: true,
                isTrial: false,
                subscribedUntil: recovered.subscribedUntil,
              });
              setSuccessUntil(recovered.subscribedUntil);
              setStep('success');
              return;
            }
          } catch {
            /* ignore */
          }
        }
        if (err.message !== 'Payment cancelled') {
          setError(err.message || t.subPaymentError);
        }
        setStep('plan');
      }
    } catch {
      setError(t.subServerError);
      setLoading(false);
      setStep('plan');
    }
  }

  if (step === 'success') {
    return (
      <div className={`sub-gate ${embedded ? 'sub-gate--embedded' : ''}`}>
        <div className="sub-gate__card sub-gate__card--center sub-gate__card--success">
          <div className="sub-gate__success-icon" aria-hidden>
            ✓
          </div>
          <h2 className="sub-gate__status-title">{t.paymentSuccessTitle}</h2>
          <p className="sub-gate__status-text">
            {successUntil
              ? t.paymentSuccessUntil.replace('{date}', formatUntil(successUntil))
              : t.paymentSuccessBody}
          </p>
          <p className="sub-gate__status-receipt">
            {emailSent
              ? (t.paymentSuccessEmailSent || 'Billing receipt emailed to {email}.').replace(
                  '{email}',
                  user?.email || ''
                )
              : t.paymentSuccessReceipt}
          </p>
          <div className="sub-gate__success-actions">
            {receiptUrl && (
              <a
                href={receiptUrl}
                className="sub-gate__receipt-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.paymentSuccessReceiptLink || 'View / download billing receipt'}
              </a>
            )}
            <a href="/#paper" className="sub-gate__pay" onClick={embedded && onClose ? onClose : undefined}>
              {t.paymentSuccessCta}
            </a>
            <p className="sub-gate__success-contact">
              <a href={businessTelHref()}>{businessPhoneDisplay()}</a>
              {' · '}
              <a href={businessWhatsAppHref()} target="_blank" rel="noreferrer">
                WhatsApp
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div className={`sub-gate ${embedded ? 'sub-gate--embedded' : ''}`}>
        <div className="sub-gate__card sub-gate__card--center">
          <div className="sub-gate__spinner-lg" aria-hidden />
          <h2 className="sub-gate__status-title" id="sub-modal-title">
            {t.subProcessingTitle}
          </h2>
          <p className="sub-gate__status-text">{t.subProcessingText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`sub-gate ${embedded ? 'sub-gate--embedded' : ''}`}>
      <div className="sub-gate__shell">
        {!embedded && (
          <aside className="sub-gate__aside">
            <img src="/rk-logo.webp" alt="" className="sub-gate__logo" />
            <h1 className="sub-gate__headline">{t.subUnlockHeadline}</h1>
            <p className="sub-gate__lead">{t.subUnlockLead}</p>
            <ul className="sub-gate__bullets">
              <li>{t.subBullet1}</li>
              <li>{t.subBullet2}</li>
              <li>{t.subBullet3}</li>
              <li>{t.subBullet4}</li>
            </ul>
          </aside>
        )}

        <main className="sub-gate__main">
          <div className={`sub-gate__card${embedded ? ' sub-gate__card--modal' : ''}`}>
            {embedded && (
              <div className="sub-modal-hero">
                {onClose && (
                  <button type="button" className="sub-modal__close" onClick={onClose} aria-label="Close">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" aria-hidden>
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
                <img src="/rk-logo.webp" alt="" className="sub-modal-hero__logo" />
                <h2 className="sub-modal-hero__title" id="sub-modal-title">
                  {t.subModalTitle}
                </h2>
                <p className="sub-modal-hero__sub">{t.subModalSub}</p>
              </div>
            )}

            <div className={embedded ? 'sub-modal-body' : 'sub-gate__card-flow'}>
              <div className={`sub-gate__user${embedded ? ' sub-gate__user--chip' : ''}`}>
                {embedded && (
                  <span className="sub-gate__user-avatar" aria-hidden>
                    {(user?.email?.[0] || '?').toUpperCase()}
                  </span>
                )}
                <div className="sub-gate__user-body">
                  <span className="sub-gate__user-label">{t.subSignedInAs}</span>
                  <span className="sub-gate__user-email">{user?.email}</span>
                  <button type="button" className="sub-gate__switch" onClick={logout}>
                    {t.subSwitchAccount}
                  </button>
                </div>
              </div>

              <div className={`sub-gate__plan${embedded ? ' sub-gate__plan--modal' : ''}`}>
                <span className="sub-gate__plan-tag">{t.subAnnualPass}</span>
                <div className="sub-gate__price">
                  {embedded ? (
                    <>
                      <span className="sub-gate__price-amount">₹{TOTAL.toFixed(2)}</span>
                      <span className="sub-gate__price-period">{t.subPerYear}</span>
                    </>
                  ) : (
                    <>
                      <span className="sub-gate__price-amount">₹{BASE}</span>
                      <span className="sub-gate__price-period">{t.subPerYear}</span>
                    </>
                  )}
                </div>
                {embedded ? (
                  <p className="sub-gate__price-total">
                    ₹{BASE} + {t.subGstLabel} ₹{GST.toFixed(2)}
                  </p>
                ) : (
                  <>
                    <p className="sub-gate__price-total">
                      {t.subTotalDue}: <strong>₹{TOTAL.toFixed(2)}</strong>
                      <span className="sub-gate__price-gst"> ({t.subGstLabel} ₹{GST.toFixed(2)})</span>
                    </p>
                    <p className="sub-gate__price-note">{t.subLinkedGmail}</p>
                  </>
                )}

                {embedded ? (
                  <p className="sub-modal-includes">
                    {t.subBullet1} · {t.subBullet2}
                  </p>
                ) : (
                  <>
                    <SubscribeBillingNotice />
                    <div className="sub-gate__breakdown">
                      <div className="sub-gate__row">
                        <span>{t.subPlanPrice}</span>
                        <span>₹{BASE.toFixed(2)}</span>
                      </div>
                      <div className="sub-gate__row">
                        <span>{t.subGstLabel}</span>
                        <span>₹{GST.toFixed(2)}</span>
                      </div>
                      <div className="sub-gate__row sub-gate__row--total">
                        <span>{t.subTotalDue}</span>
                        <span>₹{TOTAL.toFixed(2)}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className={`sub-gate__wa-field${embedded ? ' sub-gate__wa-field--modal' : ''}`}>
                <label htmlFor="sub-whatsapp-phone">
                  {t.subWhatsappLabel || 'WhatsApp number'}
                </label>
                <input
                  id="sub-whatsapp-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder={t.subWhatsappPlaceholder || '9876543210'}
                  value={whatsappPhone}
                  onChange={(e) => setWhatsappPhone(e.target.value)}
                />
                <label className="sub-gate__wa-optin">
                  <input
                    type="checkbox"
                    checked={whatsappOptIn}
                    onChange={(e) => setWhatsappOptIn(e.target.checked)}
                  />
                  <span>
                    {t.subWhatsappOptIn ||
                      'Send me a WhatsApp when today’s paper is live'}
                  </span>
                </label>
              </div>

              {error && (
                <p className="sub-gate__error" role="alert">
                  {error}
                </p>
              )}

              {isDemoMode && <p className="sub-gate__demo">{t.subDemoMode}</p>}

              {!isDemoMode && razorpayTestMode && (
                <div className="sub-gate__test-help" role="note">
                  <strong>{t.subTestModeTitle}</strong>
                  <p>{t.subTestModeUpiNote}</p>
                  <ul>
                    <li>{t.subTestModeUpiItem}</li>
                    <li>{t.subTestModeCardItem}</li>
                  </ul>
                  <p className="sub-gate__test-help-foot">{t.subTestModeFoot}</p>
                </div>
              )}

              {!isDemoMode && !razorpayTestMode && !embedded && (
                <p className="sub-gate__live-pay">
                  <span className="sub-gate__live-pay-icon" aria-hidden>
                    🔒
                  </span>
                  {t.subSecuredRazorpay}
                </p>
              )}

              <button
                type="button"
                className={`sub-gate__pay${embedded ? ' sub-gate__pay--modal' : ''}`}
                onClick={handleSubscribe}
                disabled={loading}
              >
                {loading ? <span className="sub-gate__spinner" aria-hidden /> : null}
                {loading ? t.subPleaseWait : t.subPayUnlock.replace('{total}', TOTAL.toFixed(2))}
              </button>

              {embedded ? (
                <>
                  <p className="sub-gate__secure">
                    {t.subSecuredRazorpay}. {t.subInstantAccess}
                  </p>
                  <nav className="sub-modal-legal" aria-label="Legal policies">
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      {t.footerTerms}
                    </a>
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                      {t.footerPrivacy}
                    </a>
                    <a href="/refund" target="_blank" rel="noopener noreferrer">
                      {t.footerRefund}
                    </a>
                  </nav>
                  <SubscribeBillingNotice />
                </>
              ) : (
                <>
                  <p className="sub-gate__secure">{t.subInstantAccess}</p>
                  <p className="sub-gate__legal-links">
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      {t.footerTerms}
                    </a>
                    {' · '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                      {t.footerPrivacy}
                    </a>
                    {' · '}
                    <a href="/refund" target="_blank" rel="noopener noreferrer">
                      {t.footerRefund}
                    </a>
                    {' · '}
                    <a href="/cancellation" target="_blank" rel="noopener noreferrer">
                      {t.footerCancellation}
                    </a>
                    {' · '}
                    <a href="/contact" target="_blank" rel="noopener noreferrer">
                      {t.contact}
                    </a>
                  </p>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
